// Double Ratchet (Signal). Gives 1:1 conversations forward secrecy (a device
// compromise can't decrypt already-erased past messages) and post-compromise
// security (the DH ratchet heals the session after both sides step forward).
//
// Header KDFs use domain-separated keyed BLAKE2b in place of Signal's HMAC-SHA256;
// the structure (root chain + symmetric chain) is identical.
import { concatBytes, canonicalize, utf8, toB64, fromB64 } from "@shared/encoding.js";
import { CTX, KEY_BYTES, MAX_SKIP } from "@shared/constants.js";
import { type KeyPair, genX25519, dh, kdf, hash, aeadSeal, aeadOpen, wipe, equal } from "./sodium.js";
import { pad, unpad } from "@shared/padding.js";

export interface RatchetState {
  rk: Uint8Array; // root key
  dhs: KeyPair; // our current ratchet keypair (sending)
  dhr: Uint8Array | null; // remote ratchet public key (receiving)
  cks: Uint8Array | null; // sending chain key
  ckr: Uint8Array | null; // receiving chain key
  ns: number; // messages sent in current sending chain
  nr: number; // messages received in current receiving chain
  pn: number; // length of the previous sending chain
  skipped: Map<string, Uint8Array>; // "dhB64:n" -> message key (out-of-order tolerance)
  ad: Uint8Array; // X3DH identity associated data, bound into every message
}

export interface WireHeader {
  dh: string; // base64 ratchet public key
  pn: number;
  n: number;
  nonce: string; // base64 AEAD nonce
}

// ---- KDFs ------------------------------------------------------------------

function kdfRoot(rk: Uint8Array, dhOut: Uint8Array): { rk: Uint8Array; ck: Uint8Array } {
  const mixed = hash(dhOut, KEY_BYTES, rk); // PRF(rk, dhOut)
  return { rk: kdf(mixed, CTX.rootKdf + "/rk"), ck: kdf(mixed, CTX.rootKdf + "/ck") };
}

function kdfChain(ck: Uint8Array): { ck: Uint8Array; mk: Uint8Array } {
  return { ck: kdf(ck, CTX.chainKdf), mk: kdf(ck, CTX.msgKey) };
}

const skipId = (dhB64: string, n: number): string => `${dhB64}:${n}`;

function aad(state: RatchetState, dhB64: string, pn: number, n: number): Uint8Array {
  return concatBytes(state.ad, utf8(canonicalize({ dh: dhB64, pn, n })));
}

// ---- Initialization --------------------------------------------------------

/** Initiator (Alice): immediately performs a sending-side DH ratchet against the SPK. */
export function initInitiator(sk: Uint8Array, remoteRatchetPub: Uint8Array, ad: Uint8Array): RatchetState {
  const dhs = genX25519();
  const { rk, ck } = kdfRoot(sk, dh(dhs.priv, remoteRatchetPub));
  return { rk, dhs, dhr: remoteRatchetPub, cks: ck, ckr: null, ns: 0, nr: 0, pn: 0, skipped: new Map(), ad };
}

/** Responder (Bob): his signed-prekey keypair is his initial ratchet key. */
export function initResponder(sk: Uint8Array, spk: KeyPair, ad: Uint8Array): RatchetState {
  return { rk: sk, dhs: spk, dhr: null, cks: null, ckr: null, ns: 0, nr: 0, pn: 0, skipped: new Map(), ad };
}

// ---- Encrypt / decrypt -----------------------------------------------------

export function ratchetEncrypt(state: RatchetState, plaintext: Uint8Array): { header: WireHeader; ct: string } {
  if (!state.cks) throw new Error("no sending chain yet - receive a message before sending");
  const { ck, mk } = kdfChain(state.cks);
  state.cks = ck;
  const n = state.ns++;
  const dhB64 = toB64(state.dhs.pub);
  const { nonce, ct } = aeadSeal(mk, pad(plaintext), aad(state, dhB64, state.pn, n));
  wipe(mk);
  return { header: { dh: dhB64, pn: state.pn, n, nonce: toB64(nonce) }, ct: toB64(ct) };
}

export function ratchetDecrypt(state: RatchetState, header: WireHeader, ctB64: string): Uint8Array {
  const ct = fromB64(ctB64);
  const nonce = fromB64(header.nonce);

  // 1. A message we already skipped past (arrived late).
  const sid = skipId(header.dh, header.n);
  const cached = state.skipped.get(sid);
  if (cached) {
    state.skipped.delete(sid);
    const plain = unpad(aeadOpen(cached, nonce, ct, aad(state, header.dh, header.pn, header.n)));
    wipe(cached);
    return plain;
  }

  // 2. A new ratchet public key -> finish the old chain, then DH ratchet.
  const dhBytes = fromB64(header.dh);
  if (!state.dhr || !equal(dhBytes, state.dhr)) {
    skipMessageKeys(state, header.pn);
    dhRatchet(state, dhBytes);
  }

  // 3. Catch up within the current receiving chain, then derive this message key.
  skipMessageKeys(state, header.n);
  const { ck, mk } = kdfChain(state.ckr!);
  state.ckr = ck;
  state.nr++;
  const plain = unpad(aeadOpen(mk, nonce, ct, aad(state, header.dh, header.pn, header.n)));
  wipe(mk);
  return plain;
}

function skipMessageKeys(state: RatchetState, until: number): void {
  if (state.ckr == null || state.dhr == null) return;
  if (until - state.nr > MAX_SKIP) throw new Error("too many skipped messages - possible attack or data loss");
  const dhB64 = toB64(state.dhr);
  while (state.nr < until) {
    const { ck, mk } = kdfChain(state.ckr);
    state.ckr = ck;
    state.skipped.set(skipId(dhB64, state.nr), mk);
    state.nr++;
  }
}

function dhRatchet(state: RatchetState, newDhr: Uint8Array): void {
  state.pn = state.ns;
  state.ns = 0;
  state.nr = 0;
  state.dhr = newDhr;
  const recv = kdfRoot(state.rk, dh(state.dhs.priv, newDhr));
  state.rk = recv.rk;
  state.ckr = recv.ck;
  state.dhs = genX25519();
  const send = kdfRoot(state.rk, dh(state.dhs.priv, newDhr));
  state.rk = send.rk;
  state.cks = send.ck;
}

// ---- Serialization (for the encrypted local store) -------------------------

export interface SerializedRatchet {
  rk: string;
  dhs_pub: string;
  dhs_priv: string;
  dhr: string | null;
  cks: string | null;
  ckr: string | null;
  ns: number;
  nr: number;
  pn: number;
  skipped: [string, string][];
  ad: string;
}

export function serializeRatchet(s: RatchetState): SerializedRatchet {
  return {
    rk: toB64(s.rk),
    dhs_pub: toB64(s.dhs.pub),
    dhs_priv: toB64(s.dhs.priv),
    dhr: s.dhr ? toB64(s.dhr) : null,
    cks: s.cks ? toB64(s.cks) : null,
    ckr: s.ckr ? toB64(s.ckr) : null,
    ns: s.ns,
    nr: s.nr,
    pn: s.pn,
    skipped: [...s.skipped.entries()].map(([k, v]) => [k, toB64(v)]),
    ad: toB64(s.ad),
  };
}

export function deserializeRatchet(s: SerializedRatchet): RatchetState {
  return {
    rk: fromB64(s.rk),
    dhs: { pub: fromB64(s.dhs_pub), priv: fromB64(s.dhs_priv) },
    dhr: s.dhr ? fromB64(s.dhr) : null,
    cks: s.cks ? fromB64(s.cks) : null,
    ckr: s.ckr ? fromB64(s.ckr) : null,
    ns: s.ns,
    nr: s.nr,
    pn: s.pn,
    skipped: new Map(s.skipped.map(([k, v]) => [k, fromB64(v)])),
    ad: fromB64(s.ad),
  };
}
